import { type IScheduleList, type IServerList, type ScheduleParams } from '@interface/ICommon';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { publicApi } from '@utils/remote';
import { parse } from 'date-fns';

export type QueryParams = Record<string, any>;

type ReactQueryOptions<TQueryFnData = unknown, TError = unknown> =
    | Omit<UseQueryOptions<TQueryFnData, TError>, 'queryKey' | 'queryFn'>
    | undefined;

export const fetchData = async (url: string, params?: QueryParams): Promise<any> => {
    const res = await publicApi.get<UseQueryResult>(url, { params });
    return res.data;
};

export const scheduleQueryKey = (params: QueryParams) => ['schedule', 'list', params];

export const useSchedule = (params: ScheduleParams, server: string, queryOptions?: ReactQueryOptions<IScheduleList>) => {
    if (params.date) {
        const time = parse(params.date, 'yyyy-MM-dd', new Date());
        const { date, ...rest } = params;
        params = {
            ...rest,
            dateTime: getUtc(time.toISOString()),
        };
    }

    return useQuery({
        queryKey: scheduleQueryKey({ ...params, ...{ server } }),
        queryFn: () => fetchData(`/tl/v3.0/game/schedule/${server}`, params),
        ...queryOptions,
    });
};

export const useServerList = (params: { locale: string }, queryOptions?: ReactQueryOptions<IServerList[]>) => {
    return useQuery({
        queryKey: ['schedule', 'server', 'list'],
        queryFn: () => fetchData(`/tl/v2.0/game/server`, params),
        ...queryOptions,
    });
};

export const useDateList = (params: { locale: string }, queryOptions?: ReactQueryOptions<string[]>) => {
    return useQuery({
        queryKey: ['schedule', 'date', 'list'],
        queryFn: () => fetchData(`/tl/v2.0/game/schedule/term`, params),
        ...queryOptions,
    });
};

const getUtc = (ISOString: string) => {
    const utcD = new Date(ISOString);
    const utcYear = utcD.getUTCFullYear();
    const utcMonth = String(utcD.getUTCMonth() + 1).padStart(2, '0');
    const utcDate = String(utcD.getUTCDate()).padStart(2, '0');
    const utcHours = String(utcD.getUTCHours()).padStart(2, '0');
    const utcMinutes = String(utcD.getUTCMinutes()).padStart(2, '0');
    const utcSeconds = String(utcD.getUTCSeconds()).padStart(2, '0');
    const utcMilliseconds = String(utcD.getUTCMilliseconds()).padStart(3, '0');

    const formattedUTC = `${utcYear}-${utcMonth}-${utcDate} ${utcHours}:${utcMinutes}:${utcSeconds}.${utcMilliseconds}`;
    return formattedUTC;
};
